import React, { Component } from 'react';
import { compose, mapProps } from 'recompose';
import { graphql } from 'react-apollo';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import Participants from 'components/threepp/assessment/Question/ParticipantPopover/Participants/Participants';
import CheckboxGroup from 'components/common/CheckboxGroup/CheckboxGroup';
import ThreeppPopover from 'components/common/Popover/Popover';
import { uuid, sortUsersByCurrentFirst } from 'utils/functions';
import UserBadge from 'components/threepp/UserBadge/UserBadge';
import { FIRST_LAST } from 'selectors/formatName/selector';
import { getCurrentUser } from 'graphql/operations';
import { getCurrentUser as getCurrentUserSelector } from 'selectors/user/selector';
import InviteParticipantModal from 'components/threepp/assessment/InviteParticipantModal/InviteParticipantModal';
import { DeviceWidth, EventEmitterTypes } from 'utils/constants';
import EventEmitter from 'utils/events';

import './ParticipantPopover.scss';

export class ParticipantPopover extends Component {
  constructor(props) {
    super(props);
    this.state = {
      popoverTargetId: `PopoverTargetId-${uuid()}`,
      show: false,
      selectedParticipants: [],
      intermediateParticipants: [],
      showParticipantModal: false
    };
  }

  resize = () => this.setState({ isMobile: window.innerWidth <= DeviceWidth.MOBILE });

  componentDidMount() {
    this.resize();
    window.addEventListener('resize', this.resize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resize);
  }

  getParticipantOptions() {
    const { currentUser, assessmentParticipants } = this.props;

    // current user is ALWAYS a participant on the assessment.
    const sortedParticipants = [...assessmentParticipants].sort(sortUsersByCurrentFirst(currentUser));

    const participantOptions = sortedParticipants.map(user => ({
      value: user.email,
      label: <UserBadge user={user} format={FIRST_LAST} />
    }));

    return participantOptions;
  }

  toggle = () => {
    const { initialAssignees, initialPartialAssignees } = this.props;

    const selectedParticipants = initialAssignees.map(user => user.email);
    const intermediateParticipants = initialPartialAssignees.map(user => user.email);
    this.setState(prevState => ({
      show: !prevState.show,
      selectedParticipants,
      intermediateParticipants
    }));
  };

  assignParticipants = selected => {
    const { initialAssignees, initialPartialAssignees, onAssigneesChanged } = this.props;
    let addedParticipants;
    let removedParticipants;

    this.setState(
      prevState => {
        // remove selected users from partial-selection
        const intermediateParticipants = _.without(prevState.intermediateParticipants, ...selected);

        const initialParticipantsEmail = initialAssignees.map(user => user.email);
        const initialIntermediateParticipantsEmail = initialPartialAssignees.map(user => user.email);

        // added participants are calculated from initial participant list
        addedParticipants = _.without(selected, ...initialParticipantsEmail);

        // removed participants are calculated from initial participant list and intermediate participant list
        removedParticipants = _.without(initialParticipantsEmail, ...selected);

        // Find initial intermediate participants delta
        const noLongerIntermediate = _.difference(initialIntermediateParticipantsEmail, intermediateParticipants);
        // Find removed initial intermediate participants
        const noLongerIntermediateOrSelected = _.without(noLongerIntermediate, ...addedParticipants);
        removedParticipants = _.union(noLongerIntermediateOrSelected, removedParticipants);

        return {
          selectedParticipants: selected,
          intermediateParticipants
        };
      },
      () => {
        onAssigneesChanged(addedParticipants, removedParticipants);
      }
    );
  };

  toggleAddParticipantsModal = () => {
    const { isMobile } = this.state;
    if (isMobile) {
      EventEmitter.dispatch(EventEmitterTypes.TOGGLE_MOBILE_SIDEBAR, { right: 'right', open: false });
      EventEmitter.dispatch(EventEmitterTypes.TOGGLE_ADD_PARTICIPANTS, { openSidebar: true });
    }
    this.setState(prevState => ({ showParticipantModal: !prevState.showParticipantModal, show: false }));
  };

  render() {
    const { header, hideIfNoParticipants, disabled, initialAssignees, initialPartialAssignees, onAssigneesChanged, t } = this.props;
    const { show, selectedParticipants, intermediateParticipants, popoverTargetId, showParticipantModal } = this.state;

    // Track all partially assigned and fully assigned users
    const allCurrentParticipants = _.uniqBy(initialAssignees.concat(initialPartialAssignees), 'email');
    const popoverContent = (
      <div className="popover-content">
        {header && <div className="participant-popover-header">{header}</div>}
        <CheckboxGroup
          disabled={disabled}
          options={this.getParticipantOptions()}
          checked={selectedParticipants}
          intermediate={intermediateParticipants}
          onChange={this.assignParticipants}
        />
        {!disabled && (
          // TODO: Fix this...
          /* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events  */
          <div onClick={this.toggleAddParticipantsModal} className="add-participant-link">
            {t('assessment.addParticipants')}
          </div>
        )}
      </div>
    );

    return (
      <React.Fragment>
        <InviteParticipantModal
          toggle={this.toggleAddParticipantsModal}
          isOpen={showParticipantModal}
          onAssigneesChanged={onAssigneesChanged}
          assignParticipant
        />
        <ThreeppPopover toggle={this.toggle} isOpen={show} className="participant-popover" target={popoverTargetId} placement="auto" trigger="legacy">
          {popoverContent}
        </ThreeppPopover>

        <div id={popoverTargetId}>
          <Participants users={allCurrentParticipants} hideIfNoParticipants={show ? false : hideIfNoParticipants} />
        </div>
      </React.Fragment>
    );
  }
}

ParticipantPopover.propTypes = {
  disabled: PropTypes.bool,
  header: PropTypes.string,
  // TODO: Fix this...
  /* eslint-disable react/forbid-prop-types */
  initialAssignees: PropTypes.array,
  initialPartialAssignees: PropTypes.array,
  assessmentParticipants: PropTypes.array,
  hideIfNoParticipants: PropTypes.bool,
  onAssigneesChanged: PropTypes.func.isRequired,
  currentUser: PropTypes.object.isRequired,
  /* eslint-enable react/forbid-prop-types */
  t: PropTypes.func.isRequired
};

ParticipantPopover.defaultProps = {
  disabled: false,
  header: '',
  initialAssignees: [],
  initialPartialAssignees: [],
  assessmentParticipants: [],
  hideIfNoParticipants: true
};

export const ParticipantPopoverHOC = compose(
  withTranslation(),
  graphql(getCurrentUser, { name: 'getCurrentUser' }),
  mapProps(props => ({
    disabled: props.disabled,
    header: props.header,
    initialAssignees: props.initialAssignees,
    initialPartialAssignees: props.initialPartialAssignees,
    assessmentParticipants: props.assessmentParticipants,
    onAssigneesChanged: props.onAssigneesChanged,
    currentUser: getCurrentUserSelector(props),
    hideIfNoParticipants: props.hideIfNoParticipants,
    t: props.t
  }))
);

export default ParticipantPopoverHOC(ParticipantPopover);
